/***
*
*   ADD-ONS EDITOR
*   Select and configure add-ons for a plan
*   Links add-ons to plan features via feature_key
*
**********/

import { useState, useEffect } from 'react';
import { Icon } from 'components/lib';
// import Style from './bundlePackaging.tailwind.js';

export function AddOnsEditor({ selectedAddOns, availableAddOns, planFeatures, onChange }) {
  const [localSelectedAddOns, setLocalSelectedAddOns] = useState([]);

  useEffect(() => {
    setLocalSelectedAddOns(selectedAddOns || []);
  }, [selectedAddOns]);

  const addOnsList = availableAddOns || {};
  const addOnKeys = Object.keys(addOnsList);

  const handleToggleAddOn = (addOnKey) => {
    let newSelected;
    if (localSelectedAddOns.includes(addOnKey)) {
      newSelected = localSelectedAddOns.filter(key => key !== addOnKey);
    } else {
      newSelected = [...localSelectedAddOns, addOnKey];
    }
    setLocalSelectedAddOns(newSelected);
    onChange(newSelected, planFeatures);
  };

  const formatPrice = (addOn) => {
    const symbol = addOn.currency?.symbol || '$';

    if (addOn.type === 'one_time') {
      if (addOn.price_min) {
        return `From ${symbol}${addOn.price_min}`;
      }
      return `${symbol}${addOn.price || 0}`;
    }

    if (addOn.total_unit) {
      return `${symbol}${addOn.price}/${addOn.total_unit} ${addOn.unit || 'unit'}/${addOn.interval || 'month'}`;
    }

    return `${symbol}${addOn.price}/${addOn.interval || 'month'}`;
  };

  const getPricingTypeLabel = (addOn) => {
    if (addOn.type === 'one_time') return 'One-time';
    if (addOn.total_unit) return 'Per Unit';
    return 'Recurring';
  };

  const getPricingTypeColor = (addOn) => {
    if (addOn.type === 'one_time') return 'orange';
    if (addOn.total_unit) return 'blue';
    return 'green';
  };

  if (addOnKeys.length === 0) {
    return (
      <div className="text-center py-8">
        <Icon image="package" size={48} className="mx-auto mb-4 text-slate-300" />
        <p className="text-slate-500">No add-ons available</p>
        <p className="text-sm text-slate-400 mt-1">Create add-ons in the Add-ons tab first</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
        <Icon image="package" color="blue" size={16} />
        <span>{localSelectedAddOns.length} add-ons selected for this plan</span>
      </div>

      {/* Add-ons List */}
      <div className="space-y-3">
        {addOnKeys.map((addOnKey) => {
          const addOn = addOnsList[addOnKey];
          const isSelected = localSelectedAddOns.includes(addOnKey);

          return (
            <div
              key={addOnKey}
              className={`p-4 rounded-lg border-2 transition-colors cursor-pointer ${isSelected
                  ? 'border-brand-500 bg-brand-50'
                  : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              onClick={() => handleToggleAddOn(addOnKey)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleToggleAddOn(addOnKey)}
                    className="mt-1 w-4 h-4 text-brand-500 rounded border-slate-300 focus:ring-brand-500"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div>
                    <h4 className="font-medium text-slate-800">{addOn.name}</h4>
                    <p className="text-sm text-slate-500 mt-0.5">{addOn.description}</p>

                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className={`text-xs px-2 py-0.5 rounded bg-${getPricingTypeColor(addOn)}-100 text-${getPricingTypeColor(addOn)}-700`}>
                        {getPricingTypeLabel(addOn)}
                      </span>
                      {addOn.feature_key && (
                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                          <Icon image="link" size={10} className="inline mr-1" />
                          {addOn.feature_key}
                        </span>
                      )}
                      {addOn.meter && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                          <Icon image="activity" size={10} className="inline mr-1" />
                          Metered
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="text-right">
                  <span className="font-semibold text-green-600">{formatPrice(addOn)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {localSelectedAddOns.length > 0 && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-700">
            <Icon image="info" color="blue" size={14} className="inline mr-1" />
            Selected add-ons will be available for purchase by organizations on this plan.
            Add-ons with feature links will update the corresponding feature limits when purchased.
          </p>
        </div>
      )}
    </div>
  );
}